import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { getMyBookings, getMyPackages, cancelBooking } from "@/lib/booking.functions";
import {
  cancelEventRegistration,
  getMyEventRegistrations,
} from "@/lib/event-registration.functions";
import type { MyBooking } from "@/lib/booking-display";
import {
  compareUtcIso,
  formatSiteDateTime,
  formatSiteDateTimeLong,
  formatSiteEventRange,
  isUtcIsoAfter,
  isUtcIsoBefore,
} from "@/lib/datetime.schedule";
import { checkMyRoles } from "@/lib/role.functions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { localizePriceLabel } from "@/lib/localize-price-label";

const myBookingsSearchSchema = z.object({
  book: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_authenticated/my-bookings")({
  validateSearch: myBookingsSearchSchema,
  beforeLoad: async ({ search }) => {
    const roles = await checkMyRoles();
    if (roles.isAdmin) {
      throw redirect({
        to: "/admin/bookings",
        search: search.book === 1 ? { book: 1 } : {},
      });
    }
    if (roles.isEmployee) {
      throw redirect({ to: "/employee/lessons" });
    }
  },
  head: () =>
    pageHead({
      title: m.bookings_meta_title(),
      description: m.bookings_meta_description(),
      path: "/my-bookings",
      noindex: true,
    }),
  component: MyBookingsPage,
});

function MyBookingsPage() {
  return (
    <>
      <section className="bg-ink page-band-pad text-foreground">
        <div className="container-page">
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            {m.bookings_eyebrow()}
          </span>
          <h1 className="mt-2 font-display text-4xl font-bold">{m.bookings_title()}</h1>
        </div>
      </section>

      <section className="container-page page-content-pad">
        <MemberEventsView />
        <MemberBookingsView />
      </section>
    </>
  );
}

function MemberEventsView() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["my-event-registrations"],
    queryFn: () => getMyEventRegistrations(),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelEventRegistration({ data: { id } }),
    onSuccess: () => {
      toast.success(m.bookings_toast_event_cancelled());
      qc.invalidateQueries({ queryKey: ["my-event-registrations"] });
      qc.invalidateQueries({ queryKey: ["my-event-registration-ids"] });
      qc.invalidateQueries({ queryKey: ["event-registration-counts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const registrations = data ?? [];
  const upcoming = registrations.filter(
    (r) => r.status === "confirmed" && r.site_events && isUtcIsoAfter(r.site_events.end_at),
  );
  const past = registrations.filter(
    (r) => r.status !== "confirmed" || !r.site_events || isUtcIsoBefore(r.site_events.end_at),
  );

  return (
    <>
      <h2 className="font-display text-xl font-bold">{m.bookings_events_heading()}</h2>
      {isLoading && <p className="mt-3 text-muted-foreground">{m.common_loading()}</p>}
      {!isLoading && upcoming.length === 0 && (
        <div className="mt-3 rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          {m.bookings_no_events()}{" "}
          <Link to="/events" className="text-accent hover:underline">
            {m.bookings_browse_events()}
          </Link>
        </div>
      )}
      <div className="mt-4 space-y-3">
        {upcoming.map((r) => (
          <div
            key={r.id}
            className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card p-5"
          >
            <div>
              <div className="font-semibold">{r.site_events?.title}</div>
              <div className="text-sm text-muted-foreground">
                {r.site_events?.date_label}
                {r.site_events &&
                  ` · ${formatSiteEventRange(r.site_events.start_at, r.site_events.end_at)}`}
              </div>
              <div className="text-sm text-muted-foreground">{r.registrant_name}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => cancelMut.mutate(r.id)}>
              {m.bookings_cancel()}
            </Button>
          </div>
        ))}
      </div>

      {past.length > 0 && (
        <>
          <h2 className="mt-12 font-display text-xl font-bold">{m.bookings_past_events()}</h2>
          <div className="mt-4 space-y-2">
            {past.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-lg border border-border p-4 text-sm"
              >
                <div>
                  <span className="font-medium">
                    {r.site_events?.title ?? m.bookings_event_fallback()}
                  </span>
                  {r.site_events && (
                    <span className="ml-2 text-muted-foreground">
                      {formatSiteDateTimeLong(r.site_events.start_at)}
                    </span>
                  )}
                </div>
                <span className="text-xs uppercase text-muted-foreground">{r.status}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function MemberBookingsView() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => getMyBookings(),
  });
  const packagesQ = useQuery({ queryKey: ["my-packages"], queryFn: () => getMyPackages() });
  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelBooking({ data: { id } }),
    onSuccess: () => {
      toast.success(m.bookings_toast_cancelled());
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
      qc.invalidateQueries({ queryKey: ["my-packages"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const allBookings = data ?? [];
  const upcoming = allBookings.filter((b) => isUtcIsoAfter(b.start_at) && b.status !== "cancelled");
  const past = allBookings.filter((b) => isUtcIsoBefore(b.start_at) || b.status === "cancelled");
  const activePackages = packagesQ.data ?? [];

  return (
    <>
      {isLoading && <p className="text-muted-foreground">{m.common_loading()}</p>}

      {activePackages.length > 0 && (
        <>
          <h2 className="font-display text-xl font-bold">{m.bookings_active_packages()}</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {activePackages.map((pkg) => (
              <div
                key={pkg.id}
                className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card p-5"
              >
                <div>
                  <div className="font-semibold">{localizePriceLabel(pkg.label)}</div>
                  <div className="text-sm text-muted-foreground">
                    {m.bookings_sessions_left({
                      remaining: pkg.sessions_remaining,
                      total: pkg.sessions_total,
                      minutes: pkg.duration_minutes,
                    })}
                  </div>
                </div>
                <Button
                  asChild
                  size="sm"
                  className="bg-accent text-accent-foreground hover:bg-ember-hot"
                >
                  <Link to="/schedule" search={{ package: pkg.id }}>
                    {m.bookings_book_next()}
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <h2 className={cnHeading(activePackages.length > 0, true)}>
        {m.bookings_upcoming_lessons()}
      </h2>
      {upcoming.length === 0 && (
        <div className="mt-3 rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          {m.bookings_no_lessons()}{" "}
          <Link to="/schedule" className="text-accent hover:underline">
            {m.bookings_book_one()}
          </Link>
        </div>
      )}
      <div className="mt-4 space-y-3">
        {upcoming.map((b) => {
          const pkgLabel = packageSessionLabelLocalized(b, allBookings);
          return (
            <div
              key={b.id}
              className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card p-5"
            >
              <div>
                <div className="font-semibold">{b.lesson_types?.name}</div>
                <div className="text-sm text-muted-foreground">
                  {formatSiteDateTime(b.start_at)} · {b.student_name}
                </div>
                {pkgLabel && <div className="mt-1 text-xs text-accent">{pkgLabel}</div>}
                {b.status === "pending" && (
                  <div className="mt-1 text-xs font-medium uppercase tracking-wide text-amber-600">
                    {m.bookings_pending()}
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => cancelMut.mutate(b.id)}>
                {m.bookings_cancel()}
              </Button>
            </div>
          );
        })}
      </div>

      {past.length > 0 && (
        <>
          <h2 className="mt-12 font-display text-xl font-bold">{m.bookings_past_cancelled()}</h2>
          <div className="mt-4 space-y-2">
            {past.map((b) => {
              const pkgLabel = packageSessionLabelLocalized(b, allBookings);
              return (
                <div
                  key={b.id}
                  className="flex items-center justify-between rounded-lg border border-border p-4 text-sm"
                >
                  <div>
                    <span className="font-medium">{b.lesson_types?.name}</span>
                    <span className="ml-2 text-muted-foreground">
                      {formatSiteDateTimeLong(b.start_at)}
                    </span>
                    {pkgLabel && (
                      <div className="mt-0.5 text-xs text-muted-foreground">{pkgLabel}</div>
                    )}
                  </div>
                  <span className="text-xs uppercase text-muted-foreground">{b.status}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

function cnHeading(hasPackages: boolean, hasEventsSection = false) {
  if (hasEventsSection) return "mt-12 font-display text-xl font-bold";
  return hasPackages ? "mt-12 font-display text-xl font-bold" : "font-display text-xl font-bold";
}

function packageSessionLabelLocalized(booking: MyBooking, allBookings: MyBooking[]): string | null {
  if (!booking.member_package_id || !booking.member_packages) return null;
  const pkgBookings = allBookings
    .filter((b) => b.member_package_id === booking.member_package_id && b.status !== "cancelled")
    .sort((a, b) => compareUtcIso(a.start_at, b.start_at));
  const idx = pkgBookings.findIndex((b) => b.id === booking.id);
  if (idx < 0) return null;
  return m.bookings_package_session({
    n: idx + 1,
    total: booking.member_packages.sessions_total,
    label: booking.member_packages.label,
  });
}
